import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Copy, Check, MessageCircle, Mail, Share2, ArrowLeft, UserCircle2, Heart, ExternalLink } from 'lucide-react';
import InviteYourDoctorQR from '@/components/InviteYourDoctorQR';
import { trackPreviewEvent } from '@/lib/analytics';

// "Ask your doctor if they're on DocSite" — patient-initiated doctor onboarding.
// Patient lands here, optionally drops their first name, gets a pre-written
// WhatsApp/Email message that points the doctor at the BROCHURE (not generic
// homepage) so the doctor sees the real value pitch in one tap.
const BROCHURE_URL = 'https://docsite.care/brochure?via=patient';

function buildTemplate(patientName) {
  const signoff = patientName && patientName.trim()
    ? `— ${patientName.trim()}, your patient`
    : '— A patient who appreciates you';
  return (
    `Hi Doctor,\n\n` +
    `I came across DocSite — it gives doctors their own personal booking website (free to start, no commission, no aggregator middlemen). I'd genuinely love to book with you directly through your own site.\n\n` +
    `Could you take 2 minutes to look at what they offer? Here's the brochure:\n${BROCHURE_URL}\n\n` +
    `${signoff}`
  );
}

export default function InviteYourDoctorPage() {
  const [copied, setCopied] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [canShare, setCanShare] = useState(false);

  // navigator.share is only on mobile + secure contexts — hide the button if unsupported
  useEffect(() => {
    setCanShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
    trackPreviewEvent('invite_page_view');
  }, []);

  const templateText = useMemo(() => buildTemplate(patientName), [patientName]);

  const copyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(templateText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = templateText;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {}
      document.body.removeChild(ta);
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(templateText)}`;

  // Gmail web compose works in any browser, no mail-client setup needed.
  // Falls back gracefully — mailto target is included as a secondary option in copy.
  const gmailHref = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent('Have you seen DocSite?')}&body=${encodeURIComponent(templateText)}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'DocSite — for your doctor', text: templateText, url: BROCHURE_URL });
      } catch { /* user cancelled */ }
    }
  };

  // Render the message body with the brochure URL highlighted as an inline anchor,
  // so the patient (or anyone reviewing on this page) can click straight through.
  const renderMessageWithLink = () => {
    const parts = templateText.split(BROCHURE_URL);
    return (
      <>
        {parts[0]}
        <a
          href={BROCHURE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-600 underline decoration-sky-300 underline-offset-2 hover:text-sky-700 break-all"
          data-testid="inline-brochure-link"
        >
          {BROCHURE_URL}
        </a>
        {parts[1]}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-sky-50/30 to-emerald-50/20" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>Ask Your Doctor to Join DocSite</title>
        <meta name="description" content="Invite your doctor to DocSite with a pre-written WhatsApp message. Add your name, hit send. Free to start for doctors." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="invite-your-doctor-page">
        <Link to="/for-patients" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-for-patients">
          <ArrowLeft size={14} /> Back to For Patients
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-5">
          <Heart size={12} fill="currentColor" /> ONE-TAP INVITE
        </div>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-medium text-slate-900 leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Ask your doctor if they&apos;re on <span className="text-sky-600">DocSite</span>.
        </h1>
        <p className="text-base text-slate-600 mt-5 max-w-xl leading-relaxed">
          If your favourite doctor isn&apos;t on DocSite yet, send them this. Takes 3 seconds. The Free plan is forever free — no commissions, no tricks. The link opens the brochure so they see exactly what they get.
        </p>

        {/* QR card — for doctor-at-clinic in-person nudge (shows on patient phone, doctor scans) */}
        <InviteYourDoctorQR defaultName={patientName} className="mt-10" />

        {/* Personalisation — optional name input */}
        <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm">
          <label htmlFor="patient-name" className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2 flex items-center gap-1.5">
            <UserCircle2 size={12} /> Your name (optional — makes it personal)
          </label>
          <input
            id="patient-name"
            type="text"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            maxLength={40}
            placeholder="e.g. Priya"
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-colors"
            data-testid="patient-name-input"
          />

          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mt-6 mb-3">Pre-written message</p>
          <div
            className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-2xl p-4 border border-slate-100"
            data-testid="invite-template-text"
          >
            {renderMessageWithLink()}
          </div>

          {/* Direct brochure CTA — for anyone wanting to verify the link first */}
          <a
            href={BROCHURE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-sm font-semibold hover:bg-sky-100 transition-colors"
            data-testid="open-brochure-btn"
          >
            <ExternalLink size={14} /> Open the brochure (preview the link)
          </a>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
               onClick={() => trackPreviewEvent('invite_whatsapp_click')}
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors"
               data-testid="invite-whatsapp-btn">
              <MessageCircle size={14} /> Send on WhatsApp
            </a>
            <a href={gmailHref} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition-colors"
               data-testid="invite-email-btn">
              <Mail size={14} /> Send by Gmail
            </a>
            {canShare && (
              <button onClick={nativeShare}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
                      data-testid="invite-share-btn">
                <Share2 size={14} /> Share…
              </button>
            )}
            <button onClick={copyTemplate}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300 transition-colors"
                    data-testid="invite-copy-btn">
              {copied ? <><Check size={14} className="text-emerald-600" /> Copied</> : <><Copy size={14} /> Copy text</>}
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-3 leading-relaxed">
            <strong>WhatsApp</strong> = best for India.
            <strong> Gmail</strong> opens a pre-filled compose window in your browser (no mail-client setup needed).
            {canShare ? ' ' : ' "Share…" appears automatically on phones that support it.'}
            Or just hit "Copy text" and paste anywhere.
          </p>
        </div>

        <p className="text-xs text-slate-400 mt-6 text-center">
          Your doctor stays in full control. DocSite never contacts them on your behalf.
        </p>
      </section>
    </div>
  );
}
